import type { ResearchInformationDetailMap } from './research-information';

/**
 * 森林火灾六类典型问题、19 项信息需求的逐项详解。
 *
 * 键是“问题编号|信息名称”。文字只解释为什么需要、拿到什么、缺失会怎样、
 * 以及能说明与不能说明什么；不给出火线、蔓延速度、响应等级或撤离路线。
 */
export const forestInformationDetails: ResearchInformationDetailMap = {
  /* ---------------------------------------------------- 火情线索与早期核实 */
  'FOREST-ISSUE-01|线索位置与观测时间': {
    why: '任何火情线索都要先能回答“在哪里、什么时候看到的”。没有这两个要素，后面的比对、核实和复查都无从进行。',
    what: [
      '坐标，或可与行政区划核对的地名与方位',
      '观测时刻，以及该条数据的有效期',
      '线索来源与原始记录的编号',
    ],
    risk: '位置或时间缺失时，无法判断几条线索是否指向同一处，也无法判断线索是否已经过时，容易被当成新火点重复核实。',
    boundary: '能说明某一处在某个时刻被观察到异常；不能说明该处已经发生火灾。',
    sources: [
      {
        id: 'CN-MON-2024',
        relation: '六位一体监测与核查反馈的组织要求，可用于判断一条线索应当携带哪些要素。',
      },
      {
        id: 'NASA-FIRMS',
        relation: '说明卫星热点的位置、观测时间与数据字段，可用于核对热点记录本身。',
      },
    ],
  },
  'FOREST-ISSUE-01|多来源观察': {
    why: '单一来源的告警可能误报，也可能漏报。把卫星、视频、瞭望与巡护记录按时间并排，才能看出线索之间是互相支持还是彼此矛盾。',
    what: ['各来源的观测时刻', '各来源的原始记录，而不是转述摘要', '来源之间的差异点与差异原因'],
    risk: '只沿用一条来源，会把一个未核实的信号当成结论，也无法解释它与其他观察的矛盾之处。',
    boundary: '能说明不同来源是否一致；不能由“一致”本身推出火势大小或蔓延方向。',
    sources: [
      { id: 'CN-MON-2024', relation: '多源数据共享与核查反馈要求，可用于组织不同来源的对照。' },
      {
        id: 'CN-UAV-47054',
        relation: '无人机巡查资料的记录与安全要求，可作为空中观察条目的参照。',
      },
    ],
  },
  'FOREST-ISSUE-01|现场核实情况': {
    why: '线索最终要由具备条件的人员到现场确认或否认。核实者、核实时间与结论都需要留档，才能回答“这条线索现在到底是不是真的”。',
    what: ['是否已核实，未核实时明确写未知', '核实时间与核实人所属队伍', '核实结论及其依据'],
    risk: '未标注核实状态时，读者会把线索当成已确认的事实使用。',
    boundary: '能说明某条线索是否经过核实；不能替代专业判断，也不等于认定火场范围。',
    sources: [
      { id: 'CN-MON-2024', relation: '核查反馈机制的组织依据，可用于说明核实为何需要闭环记录。' },
      {
        id: 'CN-REG-822',
        relation: '条例对火灾报告与责任分工作出规定，可用于理解核实与上报为何要留痕。',
      },
    ],
  },

  /* ---------------------------------------------------- 气象与火势变化条件 */
  'FOREST-ISSUE-02|风向风速及变化': {
    why: '风是林火信息中最常被引用、也最容易被误用的环境因素。需要的是带地点和时刻的观测记录，而不是一句笼统的印象。',
    what: ['观测站点或数据来源', '观测时刻与数据有效期', '风向风速的连续记录，而不是单个数值'],
    risk: '用“风很大”这类描述代替记录，后续判断无法追溯，也无法与其他时段比较。',
    boundary: '能说明某时某地的风况；不能据此推算火线走向或蔓延速度。',
    sources: [
      {
        id: 'CN-MON-2024',
        relation: '监测与预警协同要求，可用于说明气象信息应如何与其他观察并列。',
      },
      {
        id: 'CN-SPEC-3437',
        relation: '信息系统对火灾与环境数据结构的参考，可用于组织风况记录的字段。',
      },
    ],
  },
  'FOREST-ISSUE-02|气温湿度与降水': {
    why: '这些数据要区分“实测”与“预报”，并写明各自的生效时间段，否则读者无法判断某条数值描述的是现在还是明天。',
    what: ['实测值及其时刻', '预报值及其有效期', '数据发布机构与更新时间'],
    risk: '把预报当成实测，或看不出数据有效期，会得出与现场不符的环境判断。',
    boundary: '能说明环境条件记录；不能由单项数值自动判定火险等级。',
    sources: [
      {
        id: 'CN-MON-2024',
        relation: '预警监测体系的组织要求，可用于理解环境信息为何要标注来源与时效。',
      },
      { id: 'CN-SPEC-3437', relation: '数据结构的行业参考，可用于组织温湿度与降水的记录项。' },
    ],
  },
  'FOREST-ISSUE-02|植被与可燃物条件': {
    why: '树种、枯落物与干燥程度决定可燃物条件。这属于需要查阅已有资料的内容，不是凭现场印象判断的。',
    what: ['林分与树种资料', '枯落物与地表干燥状况记录', '资料的调查时间与来源'],
    risk: '缺少植被资料时，容易把“看起来干”当成可燃物条件，也容易忽略资料本身的年份。',
    boundary: '能说明已有资料记载的可燃物情况；不能替代现场专业评估。',
    sources: [
      {
        id: 'CN-REG-822',
        relation: '条例把预防与巡护放在同一制度框架内，可用于说明可燃物资料为何属于日常准备。',
      },
      {
        id: 'CN-SPEC-3437',
        relation: '信息系统对业务数据结构的参考，可用于组织植被与可燃物条目。',
      },
    ],
  },
  'FOREST-ISSUE-02|前后观测记录': {
    why: '判断“变化”至少需要两个时间点。单帧图像只能说明那一刻的画面，说明不了发展过程。',
    what: ['带时间戳的连续影像或观测记录', '各次观测的位置与角度', '两次观测之间的间隔说明'],
    risk: '只有一帧时，容易把视角或光线的变化误读成火情的变化。',
    boundary: '能说明两次观测之间出现了可见差异；不能据此计算蔓延速度或预测走向。',
    sources: [
      { id: 'CN-UAV-47054', relation: '无人机巡查对资料记录的要求，可用于组织连续观测条目。' },
      { id: 'CN-SPEC-3437', relation: '热点与火灾数据结构的参考，可用于把多次观察按时间排列。' },
    ],
  },

  /* ---------------------------------------------------- 地形道路与重点保护对象 */
  'FOREST-ISSUE-03|地形与坡谷信息': {
    why: '山脊、沟谷与坡度影响观察视线、通信条件和人员通行，需要以带日期的地形资料作为讨论底图。',
    what: ['地形图及其成图日期', '山脊、沟谷与坡度的标注', '与当前影像底图的一致性核对'],
    risk: '使用没有日期的底图，会把多年前的地形当成现状，也会忽略施工或滑坡造成的改变。',
    boundary: '能说明资料记载的地形关系；不能说明地形在最近是否发生变化。',
    sources: [
      { id: 'CN-SPEC-3437', relation: '信息系统对地理与业务数据结构的参考，可用于组织地形条目。' },
      {
        id: 'CN-MON-2024',
        relation: '监测体系建设要求，可用于说明地理信息为何要与其他观察并列使用。',
      },
    ],
  },
  'FOREST-ISSUE-03|道路与通行条件': {
    why: '地图上的道路不等于车辆现在能通过。需要把“资料上有什么路”和“是否已核实可通行”分开写。',
    what: ['道路资料来源与日期', '是否已核实通行，以及核实时间', '管制、封闭或施工信息'],
    risk: '把地图道路当成可通行，会得出错误的位置可达性判断，进而影响对人员与资源位置的估计。',
    boundary: '能说明资料上存在哪些道路；不能替代现场通行核实。',
    sources: [
      { id: 'CN-SPEC-3437', relation: '数据结构参考，可用于把道路资料与核实状态分字段记录。' },
      {
        id: 'CN-REG-822',
        relation: '条例对预防与保障的规定，可用于说明道路与通行信息为何属于准备内容。',
      },
    ],
  },
  'FOREST-ISSUE-03|居民点与重要设施': {
    why: '需要知道附近有哪些聚落、设施和需要重点保护的对象，以及这些资料是什么时候的。',
    what: ['居民点与重要设施清单', '位置、规模与用途', '资料来源与更新时间'],
    risk: '资料过期会漏掉新建或已撤并的居民点，也会把已停用的设施当成仍在运行。',
    boundary: '能说明资料记载的分布情况；不能判断这些位置此刻是否有人。',
    sources: [
      {
        id: 'CN-REG-822',
        relation: '条例对重点区域与人员保障的规定，可用于理解为何要预先掌握这些位置。',
      },
      {
        id: 'CN-MON-2024',
        relation: '监测与协同要求，可用于说明重点对象信息应与其他资料一起更新。',
      },
    ],
  },

  /* ---------------------------------------------------- 人员安全与疏散线索 */
  'FOREST-ISSUE-04|人员线索与位置': {
    why: '人员信息必须区分“报告的位置”“已核实的位置”和“未知”，三者不能混在一起写。',
    what: ['人员或群体的线索来源', '位置及其核实状态', '最后一次联系或出现的时间'],
    risk: '把未经核实的位置当成事实，会让后续所有判断建立在错误前提上。',
    boundary: '能说明有哪些人员线索及其核实状态；不能据此判断人员是否安全。',
    sources: [
      {
        id: 'CN-REG-822',
        relation: '条例对人员保障与组织职责作出规定，可用于说明人员信息为何要按职责登记。',
      },
      {
        id: 'CN-SAFETY-35',
        relation: '队伍扑救安全指南强调人员安全与专业界限，可用于理解线索与结论的区别。',
      },
    ],
  },
  'FOREST-ISSUE-04|联络状态': {
    why: '能否联系上人员，直接决定信息能否更新。联络尝试本身也需要记录，而不是只在成功时留痕。',
    what: ['可用的联络方式', '每次尝试的时间与结果', '对方的反馈内容'],
    risk: '未记录失败的联络尝试，会被误认为从未联系过，导致重复尝试或遗漏。',
    boundary: '能说明某次联络是否成功；不能由一次成功推出人员一直处于安全状态。',
    sources: [
      { id: 'CN-REG-822', relation: '条例对信息报告与组织协同的规定，可用于说明联络记录的作用。' },
      {
        id: 'CN-SAFETY-35',
        relation: '安全指南对现场联络与人员保障的说明，可用于组织联络状态条目。',
      },
    ],
  },
  'FOREST-ISSUE-04|通道与风险区域': {
    why: '需要把地图信息与经过核实的管制信息放在一起，并标明哪些是资料、哪些已经确认。',
    what: ['关注区域的已知通道情况', '管制或封闭信息的来源', '核实时间与核实方'],
    risk: '未分层记录的通道信息会让读者以为某条路已经可用。',
    boundary: '能说明已知的通道与管制情况；不生成撤离路线或通行建议。',
    sources: [
      {
        id: 'CN-SAFETY-35',
        relation: '队伍安全指南强调不把研究资料直接转为行动指令，可用于说明通道信息的边界。',
      },
      {
        id: 'CN-FIRE-PLAN-2020',
        relation:
          '国家预案的历史组织结构，可用于理解通道与管制信息通常由谁掌握；引用需结合现行条例复核。',
      },
    ],
  },

  /* ---------------------------------------------------- 林区通信与信息回传 */
  'FOREST-ISSUE-05|已有通信方式': {
    why: '先要清楚现场与后方各有哪些通信手段，以及这些手段的覆盖是基于什么假设。',
    what: ['通信手段的类型与归属', '覆盖范围的假设与依据', '设备数量与配置情况'],
    risk: '把“有设备”当成“有信号”，会让联络安排建立在未经验证的假设上。',
    boundary: '能说明存在哪些通信手段；不能证明这些手段在具体林区可用。',
    sources: [
      {
        id: 'CN-MON-2024',
        relation: '监测与预警协同要求通信保障，可用于说明通信方式应纳入准备内容。',
      },
      { id: 'CN-REG-822', relation: '条例对应急保障的规定，可用于理解通信准备属于日常职责。' },
    ],
  },
  'FOREST-ISSUE-05|联络对象与链路': {
    why: '通信不是抽象的“能联系”，而是具体的人、具体的链路和具体的测试结果。',
    what: ['联络对象清单与其角色', '链路类型（无线电、公网、卫星等）', '最近一次测试时间与结果'],
    risk: '缺少测试时间，就无法判断这条链路现在是否仍然有效。',
    boundary: '能说明某次联络测试的结果；不能保证此后链路一直可用。',
    sources: [
      {
        id: 'CN-FORESTRY-RELAY',
        relation: '官方实践报道记录无人机与卫星便携站配合回传图像，可用于理解链路与测通记录。',
      },
      { id: 'CN-MON-2024', relation: '监测体系对信息共享与反馈的要求，可用于组织联络对象与结果。' },
    ],
  },
  'FOREST-ISSUE-05|中继与供电条件': {
    why: '临时中继与卫星手段受地形、电源与天气影响，需要把适用条件写清楚，而不是只写设备名称。',
    what: ['中继或卫星设备的资料', '供电方式与预计续航', '地形与天气的适用性说明'],
    risk: '忽略供电与地形条件，会把设备参数当成现场能力。',
    boundary: '能说明设备与条件的匹配情况；不代表已经部署或已经测通。',
    sources: [
      {
        id: 'CN-FORESTRY-RELAY',
        relation: '官方实践报道中的卫星便携站与无人机配合，可作为中继条件的参考。',
      },
      { id: 'CN-MON-2024', relation: '通信保障的组织要求，可用于说明中继与供电为何需要预先核实。' },
    ],
  },

  /* ---------------------------------------------------- 火源风险与日常巡护 */
  'FOREST-ISSUE-06|巡护区域和记录': {
    why: '日常巡护记录是理解火源风险的长期依据。需要能看出巡护覆盖了哪里、多久一次、发现了什么。',
    what: ['巡护区域与路线', '巡护日期与频次', '发现的问题与处理情况'],
    risk: '只有汇总数字而没有记录，无法判断哪段林区长期没有覆盖。',
    boundary: '能说明巡护做过什么；不代表该区域因此安全。',
    sources: [
      {
        id: 'CN-REG-822',
        relation: '条例把巡护与预防放在同一制度框架内，可用于说明巡护记录的性质。',
      },
      {
        id: 'CN-FORESTRY-PATROL',
        relation: '莆田村居巡护与管护用房的官方实践报道，可作为巡护记录组织的参照。',
      },
    ],
  },
  'FOREST-ISSUE-06|火源活动线索': {
    why: '需要区分“已确认的野外用火”与“尚待核实的报告”，两者的可信度完全不同。',
    what: ['线索来源与发现时间', '是否已核实，以及核实方', '核实所依据的证据'],
    risk: '把待核实报告写成既定事实，会对具体人员或行为作出不成立的判断。',
    boundary: '能说明线索的核实状态；不自动认定违法，也不认定事故原因。',
    sources: [
      {
        id: 'CN-ANHUI-5',
        relation: '安徽 5 起森林火情的官方汇编，记载了祭祀、吸烟等火源线索与过火面积。',
      },
      { id: 'CN-MON-2024', relation: '核查反馈要求，可用于说明火源线索为何要区分核实状态。' },
    ],
  },
  'FOREST-ISSUE-06|本地防火期规则': {
    why: '防火期与火源管理要求有明确的适用范围和时效，必须引用当期有效的规定。',
    what: ['当期有效规定的名称', '适用范围与区域', '发布与生效时间'],
    risk: '引用已经失效的规定，会给出错误的合规判断。',
    boundary: '能说明规则条文本身写了什么；不替代属地主管部门的解释与裁量。',
    sources: [
      {
        id: 'CN-REG-822',
        relation:
          '条例自 2026-01-01 施行，规定了预防、报告与巡护职责，可用于说明本地规则的制度来源。',
      },
    ],
  },
};
